import React, { useCallback, useState } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	ExperienceSuccess,
	SkeletonList,
	Spinner,
	useAnalytics,
	useExpTrackerStop,
	usePrincipalUser,
	UserExperienceNames,
} from '@atlassian/ptc-common';

import { useTeamSearchQuery } from '../../hooks/useTeamSearchQuery';
import InfiniteScroller from '../InfiniteScroller/InfiniteScroller';
import { AboutSectionTitle } from '../ProfileAbout';
import { type UserItemType } from '../TeamInviteDialog/types';
import TeamsProfileItem from '../TeamsProfileItem';
import TeamsProfileItemButton from '../TeamsProfileItem/TeamsProfileItemButton';

import * as Styled from './ProfileAboutTeams.styled';
import { type ProfileAboutTeamsProps } from './types';

export const PROFILE_ABOUT_TEAMS_NUM_OF_INITIAL_TEAMS = 10;
export const PROFILE_ABOUT_TEAMS_NUM_OF_TEAMS_SUBSEQUENT_CALLS = 30;

export function ProfileAboutTeams(props: ProfileAboutTeamsProps) {
	const analytics = useAnalytics();
	const [enableInfiniteScroll, setEnableInfiniteScroll] = useState(false);
	const [showLess, setShowLess] = useState(true);
	const [{ hasLoaded: hasUserDataLoaded, principalUserId: currentUserId }] = usePrincipalUser();

	const {
		isLoading: isTeamSearchLoading,
		teams: teamSearchTeams,
		error: teamSearchError,
		loadMore,
		hasMore,
	} = useTeamSearchQuery({
		first: PROFILE_ABOUT_TEAMS_NUM_OF_INITIAL_TEAMS + 1, // +1 to make sure it has more than 10 teams
		batchSize: PROFILE_ABOUT_TEAMS_NUM_OF_TEAMS_SUBSEQUENT_CALLS,
		memberIds: [props.userId],
	});

	// don't need to trigger start event here because it's already triggered in `NeedBrowseUsersPerm`
	useExpTrackerStop({
		name: UserExperienceNames.USER_PROFILE_USERS_TEAMS,
		error: teamSearchError,
	});

	const createProposedMember = useCallback<() => UserItemType[]>(
		() =>
			props.userFullName
				? [
						{
							id: props.userId,
							value: props.userId,
							label: props.userFullName,
							avatarUrl: props.userAvatarUrl,
							type: 'user',
						},
					]
				: [],
		[props.userAvatarUrl, props.userFullName, props.userId],
	);

	const handleTeamItemClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, index: number) => {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				action: AnalyticsEventActions.CLICKED,
				actionSubject: 'teamProfileItem',
				attributes: {
					position: index,
				},
			});
		},
		[analytics],
	);

	const isCurrentUser = () => currentUserId === props.userId;

	// loading --> skeleton and do not render ExperienceSuccess
	// not loading + empty data + viewing others profile --> rendering nothing and ExperienceSuccess
	// not loading + has error --> rendering nothing and "useExpTrackerStop" will trigger the stop experience event.
	// not loading + has data --> render UI and ExperienceSuccess

	const hasNoData = !teamSearchTeams;

	if (hasNoData) {
		return null;
	}

	if (teamSearchError) {
		return (
			<>
				<AboutSectionTitle>
					<FormattedMessage {...messages.teamSectionTitle} />
				</AboutSectionTitle>

				<Styled.ErrorContainer>
					<Styled.ErrorIconWrapper>
						<ErrorIcon
							LEGACY_size="medium"
							spacing="spacious"
							color={token('color.icon.danger', R400)}
							label={messages.errorStateTitle.defaultMessage}
						/>
					</Styled.ErrorIconWrapper>
					<Styled.ErrorMessageWrapper>
						<Styled.ErrorTitle>
							<FormattedMessage {...messages.errorStateTitle} />
						</Styled.ErrorTitle>
						<Styled.ErrorMessage>
							<FormattedMessage {...messages.errorStateMessage} />
						</Styled.ErrorMessage>
					</Styled.ErrorMessageWrapper>
				</Styled.ErrorContainer>
			</>
		);
	}

	const onShowMoreButtonClick = () => {
		analytics.pushUIEvent({
			source: AnalyticsEventSource.USER_PROFILE_SCREEN,
			actionSubject: 'showMore',
			action: AnalyticsEventActions.CLICKED,
		});

		setEnableInfiniteScroll(true);
		setShowLess(false);
	};

	const collapseTeamList = () => {
		if (!enableInfiniteScroll) {
			setEnableInfiniteScroll(true);
		}
		setShowLess(!showLess);
	};

	const teamList = (
		<InfiniteScroller
			loadMore={loadMore}
			hasMore={hasMore && enableInfiniteScroll}
			loader={<Spinner />}
			isLoading={isTeamSearchLoading}
		>
			<>
				{showLess
					? teamSearchTeams
							.slice(0, PROFILE_ABOUT_TEAMS_NUM_OF_INITIAL_TEAMS)
							.map((team, index) => (
								<TeamsProfileItem
									key={team.id}
									team={team}
									isCurrentUser={isCurrentUser()}
									index={index}
									onClick={handleTeamItemClick}
								/>
							))
					: teamSearchTeams.map((team, index) => (
							<TeamsProfileItem
								key={team.id}
								team={team}
								isCurrentUser={isCurrentUser()}
								index={index}
								onClick={handleTeamItemClick}
							/>
						))}
				{!enableInfiniteScroll &&
					teamSearchTeams.length > PROFILE_ABOUT_TEAMS_NUM_OF_INITIAL_TEAMS &&
					(hasMore || (!hasMore && showLess)) && (
						<Styled.ShowMoreButton>
							<Button appearance="link" spacing="none" onClick={onShowMoreButtonClick}>
								<FormattedMessage {...messages.showMore} />
							</Button>
						</Styled.ShowMoreButton>
					)}
				{enableInfiniteScroll && !hasMore && (
					<Styled.ShowMoreButton>
						<Button appearance="link" spacing="none" onClick={collapseTeamList}>
							{showLess ? (
								<FormattedMessage {...messages.showMore} />
							) : (
								<FormattedMessage {...messages.showLess} />
							)}
						</Button>
					</Styled.ShowMoreButton>
				)}
			</>
		</InfiniteScroller>
	);
	return (
		<>
			<AboutSectionTitle>
				<FormattedMessage {...messages.teamSectionTitle} />
			</AboutSectionTitle>

			<Styled.AboutTeamsList>
				{/* Show Start a team button on the top of the list */}
				{hasUserDataLoaded && (
					<TeamsProfileItemButton
						source="teamsList"
						isCurrentUser={isCurrentUser()}
						proposedMembers={isCurrentUser() ? [] : createProposedMember()}
						isActiveUser={props.isActiveUser}
					/>
				)}
				{isTeamSearchLoading ? (
					<SkeletonList
						hasAvatar
						isShimmering
						cssFn={() => ({
							paddingLeft: 0,
							paddingRight: 0,
						})}
						itemCount={3}
					/>
				) : (
					teamList
				)}
			</Styled.AboutTeamsList>

			<ExperienceSuccess
				hasLoaded={!isTeamSearchLoading && !teamSearchError}
				name={UserExperienceNames.USER_PROFILE_USERS_TEAMS}
			/>
		</>
	);
}

export const messages = defineMessages({
	teamSectionTitle: {
		id: 'ptc-directory.user-profile-page.about.teams.heading',
		defaultMessage: 'Teams',
		description: 'Heading for the teams that user is a member of',
	},
	showMore: {
		id: 'ptc-directory.user-profile-page.about.teams.show.more.button',
		defaultMessage: 'Show more',
		description: 'Text for the show more button',
	},
	showLess: {
		id: 'ptc-directory.user-profile-page.about.teams.show.less.button',
		defaultMessage: 'Show less',
		description: 'Text for the show less button',
	},
	errorStateTitle: {
		id: 'ptc-directory.user-profile-page.about.teams.error.title',
		defaultMessage: 'Something went wrong',
		description: 'Error title for when teams fail to load',
	},
	errorStateMessage: {
		id: 'ptc-directory.user-profile-page.about.teams.error.message',
		defaultMessage: 'Refresh the page or try again later',
		description: 'Error instruction for when teams fail to load',
	},
});

export default ProfileAboutTeams;
